/* eslint-disable @next/next/no-img-element */
"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const sections = [
  {
    id: "intro",
    title: "What is a Bridge Loan?",
    content: (
      <p className="text-gray-600 leading-relaxed">
        A bridge loan is a short-term financing option that allows homeowners to
        access the equity in their current home to purchase a new property
        before selling their existing one. This type of loan “bridges” the gap
        between the purchase of a new home and the sale of the old one.
      </p>
    ),
    image: "/buy-before-you-sell/Frame 2147225759 (4).png",
  },
  {
    id: "work",
    title: "How Bridge Loans Work?",
    content: (
      <ul className="list-disc pl-5 text-gray-600 leading-relaxed space-y-2">
        <li>
          <strong>Access to Equity:</strong> Homeowners can borrow against the
          equity in their current home, providing immediate funds to make a down
          payment.
        </li>
        <li>
          <strong>Flexible Terms:</strong> Many lenders offer flexible repayment
          options, allowing borrowers to pay off the loan when their current
          home sells.
        </li>
      </ul>
    ),
    image: "/buy-before-you-sell/Frame 2147225759 (2).png",
  },
  {
    id: "benefits",
    title: "Benefits of Bridge Loans",
    content: (
      <ul className="list-disc pl-5 text-gray-600 leading-relaxed space-y-2">
        <li>
          <strong>Buy Before You Sell:</strong> Purchase a new home without
          waiting for your current home to sell.
        </li>
        <li>
          <strong>Reduced Stress:</strong> Avoid the pressure of coordinating
          multiple closings.
        </li>
        <li>
          <strong>Improved Buying Power:</strong> Make stronger offers without
          contingencies.
        </li>
        <li>
          <strong>No Compromise:</strong> Find your ideal home without rushing
          to sell your existing one.
        </li>
      </ul>
    ),
    image: "/buy-before-you-sell/Frame 2147225759 (3).png",
  },
  {
    id: "considerations",
    title: "Considerations",
    content: (
      <ul className="list-disc pl-5 text-gray-600 leading-relaxed space-y-2">
        <li>
          <strong>Higher Interest Rates:</strong> Bridge loans may have higher
          rates than traditional mortgages.
        </li>
        <li>
          <strong>Repayment Plan:</strong> Borrowers should plan when and how
          they’ll repay the loan.
        </li>
        <li>
          <strong>Market Conditions:</strong> The success of a bridge loan often
          depends on current housing market trends.
        </li>
      </ul>
    ),
    image: "/buy-before-you-sell/Frame 2147225759 (4).png",
  },
];

export default function BridgeLoanPage() {
  const [activeTab, setActiveTab] = useState("intro");

  const activeSection = sections.find((s) => s.id === activeTab)!;

  return (
    <div className=" bg-[#F8FAFC] flex flex-col items-center py-20 px-4">
      {/* Title */}
      <h1 className="text-3xl md:text-4xl font-semibold text-center text-[#1E293B] mb-8">
        Understanding Bridge Loans
      </h1>

      {/* Button Tabs */}
      <div className="flex flex-wrap justify-center gap-3 mb-10">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveTab(s.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
              activeTab === s.id
                ? "bg-[#4177BC] text-white border-[#4177BC]"
                : "bg-white text-gray-700 border-gray-200 hover:bg-[#EAF2FB]"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      {/* Content Card */}
      <div className="max-w-6xl bg-white rounded-2xl shadow-xl overflow-hidden grid md:grid-cols-2 gap-0 border border-gray-100">
        {/* Text Section */}
        <div className="p-8 flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">
                {activeSection.title}
              </h2>
              <div className="text-sm md:text-base">
                {activeSection.content}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Image Section */}
        <motion.div
          key={activeSection.image}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <img
            src={activeSection.image}
            alt={activeSection.title}
            className="w-full h-full p-5 object-cover"
          />
        </motion.div>
      </div>
    </div>
  );
}
